import { supabase } from '@/integrations/supabase/client';
import { Procurement } from '@/types/database.types';
import { createProcurementAudit } from '@/services/procurementAuditService';
import { sendTelegramNotification } from '@/services/telegram/send';
import type { ItemQualityData } from '@/types/database/procurement-item-quality';

interface GradeEntry {
  grade: string;
  quantity_kg: number;
  notes: string;
}

interface QualityInspectionData {
  sampleSizeChecked?: boolean;
  sampleSizeNotes?: string;
  damageKg?: number;
  compensationKg?: number;
  damagePercentage?: number;
  damageNotes?: string;
  qualityGrade?: string;
  rejectionReason?: string;
  gradeBreakdown?: GradeEntry[];
  gradingType?: 'preliminary' | 'final';
  finalGradingExpectedDate?: string;
  preliminaryNotes?: string;
}

interface UpdateQualityStatusOptions {
  procurement: Procurement;
  inspectionData?: QualityInspectionData;
  itemQualityData?: ItemQualityData[];
  userId: string;
  action: 'approve' | 'reject';
}

export const updateQualityStatus = async ({
  procurement,
  inspectionData = {},
  itemQualityData,
  userId,
  action
}: UpdateQualityStatusOptions): Promise<{ id: string; status: string }> => {
  const currentStatus = procurement.status;
  const paymentTermType = procurement.finance?.payment_term_type || 'upfront';
  const gradingType = inspectionData.gradingType || 'final';
  const isMultiItem = (itemQualityData?.length || 0) > 0;
  
  console.log(`[Quality] Processing ${action} for procurement ${procurement.id}`);
  console.log(`[Quality] Current status: ${currentStatus}, Payment term: ${paymentTermType}, Grading type: ${gradingType}, Multi-item: ${isMultiItem}`);

  let newStatus: string;
  
  if (action === 'approve') {
    // Quality approved - move to warehouse for quantity verification
    newStatus = 'pending_warehouse';
  } else {
    // Quality rejected
    newStatus = 'rejected';
  }

  console.log(`[Quality] New status will be: ${newStatus}`);

  // Build quality update data based on grading type
  const qualityUpdateData: Record<string, any> = {
    sample_size_checked: inspectionData.sampleSizeChecked || false,
    sample_size_notes: inspectionData.sampleSizeNotes || null,
    quality_grade: inspectionData.qualityGrade || null,
    grading_type: gradingType,
    updated_at: new Date().toISOString()
  };

  // Handle preliminary vs final grading data differently
  if (gradingType === 'preliminary') {
    // Store in preliminary fields
    qualityUpdateData.preliminary_damage_kg = inspectionData.damageKg || 0;
    qualityUpdateData.preliminary_compensation_kg = inspectionData.compensationKg || 0;
    qualityUpdateData.preliminary_damage_percentage = inspectionData.damagePercentage || 0;
    qualityUpdateData.preliminary_graded_at = new Date().toISOString();
    qualityUpdateData.preliminary_notes = inspectionData.damageNotes || null;
    qualityUpdateData.final_grading_expected_date = inspectionData.finalGradingExpectedDate || null;
    qualityUpdateData.is_final_grading_complete = false;
    
    // Also set the main fields for immediate use (will be overwritten when final grading is done)
    qualityUpdateData.damage_kg = inspectionData.damageKg || 0;
    qualityUpdateData.compensation_kg = inspectionData.compensationKg || 0;
    qualityUpdateData.damage_percentage = inspectionData.damagePercentage || 0;
    qualityUpdateData.damage_notes = inspectionData.damageNotes || null;
  } else {
    // Final grading - store in main fields
    qualityUpdateData.damage_kg = inspectionData.damageKg || 0;
    qualityUpdateData.compensation_kg = inspectionData.compensationKg || 0;
    qualityUpdateData.damage_percentage = inspectionData.damagePercentage || 0;
    qualityUpdateData.damage_notes = inspectionData.damageNotes || null;
    qualityUpdateData.final_graded_at = new Date().toISOString();
    qualityUpdateData.is_final_grading_complete = true;
  }

  if (action === 'approve') {
    qualityUpdateData.quality_approved_by = userId;
    qualityUpdateData.quality_approval_date = new Date().toISOString();
  } else {
    qualityUpdateData.quality_rejected_by = userId;
    qualityUpdateData.quality_rejection_date = new Date().toISOString();
    qualityUpdateData.quality_rejection_reason = inspectionData.rejectionReason || 'Quality inspection failed';
  }

  // Check if quality record exists, create if not
  const { data: existingQuality } = await supabase
    .from('procurement_quality')
    .select('id')
    .eq('procurement_id', procurement.id)
    .maybeSingle();

  let qualityRecordId: string;

  if (existingQuality) {
    const { error: qualityError } = await supabase
      .from('procurement_quality')
      .update(qualityUpdateData)
      .eq('procurement_id', procurement.id);

    if (qualityError) {
      console.error('[Quality] Error updating procurement_quality:', qualityError);
      throw new Error(`Failed to update quality inspection data: ${qualityError.message}`);
    }
    qualityRecordId = existingQuality.id;
    console.log(`[Quality] Quality record updated with ID: ${qualityRecordId}`);
  } else {
    const { data: insertedQuality, error: insertError } = await supabase
      .from('procurement_quality')
      .insert([{ procurement_id: procurement.id, ...qualityUpdateData }])
      .select('id')
      .single();

    if (insertError) {
      console.error('[Quality] Error inserting procurement_quality:', insertError);
      throw new Error(`Failed to create quality inspection data: ${insertError.message}`);
    }
    qualityRecordId = insertedQuality.id;
    console.log(`[Quality] Quality record created with ID: ${qualityRecordId}`);
  }

  // Verify the quality record was saved correctly
  const { data: verifyRecord, error: verifyError } = await supabase
    .from('procurement_quality')
    .select('id')
    .eq('id', qualityRecordId)
    .single();

  if (verifyError || !verifyRecord) {
    console.error('[Quality] CRITICAL: Quality record not found after insert/update!', { qualityRecordId, verifyError });
    throw new Error('Quality inspection data was not saved correctly. Please try again.');
  }

  console.log(`[Quality] Verified quality record exists: ${qualityRecordId}`);

  // Save grade breakdown if provided
  if (inspectionData.gradeBreakdown && inspectionData.gradeBreakdown.length > 0) {
    // First, delete existing grade breakdown for this quality record
    await supabase
      .from('procurement_quality_grades')
      .delete()
      .eq('procurement_quality_id', qualityRecordId);

    // Insert new grade breakdown entries
    const gradeEntries = inspectionData.gradeBreakdown
      .filter(entry => entry.grade && entry.quantity_kg > 0)
      .map(entry => ({
        procurement_quality_id: qualityRecordId,
        grade: entry.grade,
        quantity_kg: entry.quantity_kg,
        notes: entry.notes || null
      }));

    if (gradeEntries.length > 0) {
      const { error: gradeError } = await supabase
        .from('procurement_quality_grades')
        .insert(gradeEntries);

      if (gradeError) {
        console.error('[Quality] Error saving grade breakdown:', gradeError);
        // Don't throw - this is non-critical
      }
    }
  }

  // Save per-item quality data for multi-item procurements
  if (itemQualityData && itemQualityData.length > 0) {
    console.log(`[Quality] Saving per-item quality data for ${itemQualityData.length} items`);
    
    // First, delete existing item quality records for this quality record
    await supabase
      .from('procurement_item_quality')
      .delete()
      .eq('procurement_quality_id', qualityRecordId);

    // Build item quality entries based on grading type
    const itemQualityEntries = itemQualityData.map(item => {
      const baseEntry = {
        procurement_quality_id: qualityRecordId,
        procurement_item_id: item.itemId,
        quality_grade: item.qualityGrade || null,
        grade_notes: item.notes || null,
        is_rejected: item.isRejected || false,
      };
      
      if (gradingType === 'preliminary') {
        return {
          ...baseEntry,
          preliminary_damage_kg: item.damageKg || 0,
          preliminary_compensation_kg: item.compensationKg || 0,
          preliminary_damage_percentage: item.damagePercentage || 0,
          damage_kg: item.damageKg || 0,
          compensation_kg: item.compensationKg || 0,
          damage_percentage: item.damagePercentage || 0
        };
      } else {
        return {
          ...baseEntry,
          damage_kg: item.damageKg || 0,
          compensation_kg: item.compensationKg || 0,
          damage_percentage: item.damagePercentage || 0
        };
      }
    });

    const { error: itemQualityError } = await supabase
      .from('procurement_item_quality')
      .insert(itemQualityEntries);

    if (itemQualityError) {
      console.error('[Quality] Error saving per-item quality data:', itemQualityError);
    } else {
      console.log(`[Quality] Successfully saved ${itemQualityEntries.length} item quality records`);
    }
  }

  // Update procurement status
  const procurementUpdateData: Record<string, any> = {
    status: newStatus,
    previous_status: currentStatus,
    updated_at: new Date().toISOString()
  };

  if (action === 'reject') {
    procurementUpdateData.rejected_by = userId;
    procurementUpdateData.rejection_source = 'quality';
  }

  const { data: updatedProcurement, error: updateError } = await supabase
    .from('procurements')
    .update(procurementUpdateData)
    .eq('id', procurement.id)
    .select()
    .single();

  if (updateError) {
    console.error('[Quality] Error updating procurement status:', updateError);
    throw new Error(`Failed to update procurement status: ${updateError.message}`);
  }

  if (!updatedProcurement) {
    throw new Error('Failed to update procurement - no data returned');
  }

  // Create audit trail
  try {
    await createProcurementAudit({
      procurement_id: procurement.id,
      action: action === 'approve' ? 'quality_approved' : 'quality_rejected',
      old_status: currentStatus,
      new_status: newStatus,
      changed_by: userId,
      metadata: {
        inspection_data: inspectionData,
        payment_term_type: paymentTermType,
        grading_type: gradingType
      }
    });
  } catch (auditError) {
    console.error('[Quality] Error creating audit trail:', auditError);
  }

  // Build grade breakdown summary for notification
  let gradeBreakdownSummary = '';
  if (inspectionData.gradeBreakdown && inspectionData.gradeBreakdown.length > 0) {
    gradeBreakdownSummary = '\n**Grade Breakdown:**\n' + 
      inspectionData.gradeBreakdown
        .filter(e => e.grade && e.quantity_kg > 0)
        .map(e => `  • ${e.grade}: ${e.quantity_kg} KG`)
        .join('\n');
  }

  // Send notification
  try {
    const actionVerb = action === 'approve' ? 'approved' : 'rejected';
    const gradingTypeLabel = gradingType === 'preliminary' ? ' (Preliminary)' : ' (Final)';
    const compensationInfo = inspectionData.damageKg && inspectionData.damageKg > 0 
      ? `\n**${gradingType === 'preliminary' ? 'Estimated ' : ''}Damage Found:** ${inspectionData.damageKg} KG\n**${gradingType === 'preliminary' ? 'Estimated ' : ''}Agreed Compensation:** ${inspectionData.compensationKg || 0} KG`
      : '';
    
    const expectedFinalDate = gradingType === 'preliminary' && inspectionData.finalGradingExpectedDate
      ? `\n**Expected Final Grading:** ${inspectionData.finalGradingExpectedDate}`
      : '';

    const notificationMessage = `🔍 **Quality Inspection ${actionVerb.charAt(0).toUpperCase() + actionVerb.slice(1)}${gradingTypeLabel}**

**Procurement ID:** ${procurement.id}
**Product:** ${procurement.product_name || 'N/A'}
**Quantity:** ${procurement.total_quantity || 'N/A'}
**Grading Type:** ${gradingType === 'preliminary' ? 'Sample-based (Preliminary)' : 'Complete (Final)'}${compensationInfo}
**Damage %:** ${inspectionData.damagePercentage || 0}%
**Quality Grade:** ${inspectionData.qualityGrade || 'N/A'}${gradeBreakdownSummary}${expectedFinalDate}
**New Status:** ${newStatus.replace('_', ' ')}

${action === 'reject' 
  ? `**Rejection Reason:** ${inspectionData.rejectionReason || 'Not specified'}` 
  : gradingType === 'preliminary' 
    ? '⚠️ Payment will proceed based on sample assessment. Final grading pending.'
    : 'Ready for warehouse quantity verification.'}`;

    await sendTelegramNotification(notificationMessage, procurement.id);
  } catch (notificationError) {
    console.error('[Quality] Error sending notification:', notificationError);
  }

  console.log(`[Quality] Successfully updated procurement ${procurement.id} to status: ${newStatus}`);

  return { id: procurement.id, status: newStatus };
};
